import React from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {Hotel} from '@findhotel/sapi'
import {FilterSource} from 'components/Accommodation/OffersFilters/constants'
import {CompactOfferList} from 'components/OfferList/CompactOfferList/CompactOfferList'
import {OfferTrackingContextProvider} from 'context/offerTracking'
import {useOfferContainer} from 'hooks/useOfferContainer'
import {getAllOffersOverlay} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {
  getAnchorPrice,
  getAnchorPriceTotal,
  getOfferEntity,
  getSearchParameters,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Stack} from '@vio/atlas/helpers/Stack'
import {OverlayPage, OverlayPageContent} from '@vio/atlas/OverlayPage'
import {PopUp} from '@vio/atlas/PopUp'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {isPasswordlessLoginVisible} from '@vio/core/src/auth/modules/selectors'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import isBofhOffer from '@vio/core/src/offer/business/isBofhOffer'

import {AnimatedDivider} from './AnimatedDivider'
import {UnavailabilityWithFilters} from './Unavailability'

const ALL_OFFERS_POPUP_MAX_WIDTH = 640

const Wrapper = styled.div`
  padding: ${cssTheme.layout.spacing.s300} 0;
  margin: ${cssTheme.layout.spacing.s000} ${cssTheme.layout.spacing.s500};
  ${linariaMq.desktopXs} {
    margin: ${cssTheme.layout.spacing.s300} 0 0;
  }
`

const DividerWrapper = styled.div`
  position: sticky;
  background-color: ${cssTheme.colors.background.neutral.primary};
  z-index: 2;
  top: 0;
`

const PopupWrapper = styled.div`
  width: calc(
    ${ALL_OFFERS_POPUP_MAX_WIDTH}px - ${cssTheme.layout.spacing.s600} * 2
  );
`

interface AllOffersOverlayContentProps extends Pick<Props, 'sourceComponent'> {
  hotelId: Hotel['objectID']
  showArrowCta?: boolean
  showTitle?: boolean
}
const AllOffersOverlayContent: React.FC<AllOffersOverlayContentProps> = ({
  sourceComponent,
  hotelId,
  showArrowCta,
  showTitle = true
}) => {
  const {isMobile} = useDeviceLayout()

  const {
    monetisation: {cashback}
  } = useBrandCapabilities()

  const {splitBookingOffers, splitBookingBundle, hasSplitBookingOffer} =
    useSelector(state => getSplitBookingDetails(state, hotelId))

  const topOffersAnchorPrice = useSelector(state =>
    getAnchorPrice(state, hotelId)
  )

  const topOffersAnchorPriceTotal = useSelector(state =>
    getAnchorPriceTotal(state, hotelId)
  )
  // split booking offer should always be at the top for All Offers Overlay
  const sbOfferPosition = 0

  // Use offer data directly from the store
  const offerEntity = useSelector(state => getOfferEntity(state, hotelId))
  const searchedParameters = useSelector(getSearchParameters)

  const {allOffers, hasOffers} = useOfferContainer({
    offerEntity,
    hotelId
  })

  const {ref: elementRef, inView} = useInView({threshold: 0})

  // remove Vio offers from the list in case there is a split booking offer
  const offersFiltered =
    splitBookingOffers.length > 0
      ? allOffers.filter(offer => !isBofhOffer(offer))
      : allOffers

  return (
    <Stack space={isMobile ? 's000' : 's500'}>
      <div ref={elementRef} />
      {!inView && (
        <DividerWrapper>
          <AnimatedDivider inView={inView} />
        </DividerWrapper>
      )}
      <Wrapper>
        <>
          {hasOffers ? (
            <>
              {showTitle && (
                <Text variant="titleL">
                  <FormattedMessage
                    id="offersAmount"
                    defaultMessage="{amount, plural, one {1 deal} other {# deals}}"
                    values={{amount: offersFiltered.length}}
                  />
                </Text>
              )}
              <OfferTrackingContextProvider
                offerEntity={offerEntity}
                parameters={searchedParameters}
              >
                <Box paddingTop="s500" paddingBottom="s500">
                  <CompactOfferList
                    showArrowCta={showArrowCta}
                    hasRoomLabels
                    splitBookingOffers={splitBookingOffers}
                    splitBookingPosition={sbOfferPosition}
                    hasSplitBookingOffer={hasSplitBookingOffer}
                    splitBookingBundle={splitBookingBundle}
                    sourceComponent={sourceComponent}
                    offers={offersFiltered}
                    hotelId={hotelId}
                    anchorPrice={topOffersAnchorPrice}
                    anchorPriceTotal={topOffersAnchorPriceTotal}
                    showCashback={cashback.enabled}
                  />
                </Box>
              </OfferTrackingContextProvider>
            </>
          ) : (
            <UnavailabilityWithFilters
              source={FilterSource.RemoveMoreDealsQuickFilters}
            />
          )}
        </>
      </Wrapper>
    </Stack>
  )
}

interface Props {
  variant?: 'popup' | 'overlay'
  sourceComponent: string
}

export const AllOffersOverlay: React.FC<Props> = ({
  variant = 'overlay',
  sourceComponent
}) => {
  const dispatch = useDispatch()
  const {formatMessage} = useIntl()
  const passwordlessLoginIsVisible = useSelector(isPasswordlessLoginVisible)

  const overlay = useSelector(getAllOffersOverlay)
  const {hotelId} = overlay || {}
  const isOpen = Boolean(overlay)

  const onClose = () => {
    /* TODO: When closing the passwordless login, the popup is closing
     * because of the OutsideClickHandler. Should be fixed in the PopUp
     * component instead.
     */
    if (passwordlessLoginIsVisible) return
    dispatch(hideOverlay())
  }

  if (variant === 'popup') {
    return (
      <PopUp
        isOpen={isOpen}
        width={ALL_OFFERS_POPUP_MAX_WIDTH}
        navBarProps={{
          centerContent: (
            <FormattedMessageWrapper
              id="hotelCard.moreDeals"
              defaultMessage="More deals"
            />
          )
        }}
        closeAction={onClose}
      >
        <PopupWrapper>
          <AllOffersOverlayContent
            hotelId={hotelId}
            sourceComponent={sourceComponent}
          />
        </PopupWrapper>
      </PopUp>
    )
  }

  return (
    <OverlayPage
      isOpen={isOpen}
      navBarProps={{
        centerContent: formatMessage({
          id: 'hotelCard.moreDeals',
          defaultMessage: 'More deals'
        })
      }}
      onClose={onClose}
      hasHeaderBoxShadow={false}
    >
      <OverlayPageContent>
        <AllOffersOverlayContent
          hotelId={hotelId}
          sourceComponent={sourceComponent}
        />
      </OverlayPageContent>
    </OverlayPage>
  )
}
